import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { AnimatePresence, motion } from "framer-motion";
import { Shield } from "lucide-react";
import {
  OPEN_PREFERENCES_EVENT,
  readConsent,
  writeConsent,
} from "@/lib/consent";
import { BRAND } from "@/constants/brand";

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className="relative inline-flex h-5 w-10 flex-shrink-0 items-center rounded-full p-0.5 transition-colors"
      style={{ background: checked ? BRAND.lime : "#D4D2CB" }}
    >
      <span
        className={`block h-4 w-4 rounded-full shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
        style={{ background: checked ? BRAND.navy : "#0C0D10" }}
      />
    </button>
  );
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const close = useCallback((analyticsOn: boolean, marketingOn: boolean) => {
    writeConsent({ analytics: analyticsOn, marketing: marketingOn });
    setOpen(false);
    setShowDetails(false);
  }, []);

  useEffect(() => {
    if (!readConsent()) {
      const timer = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const onOpenPreferences = () => {
      const existing = readConsent();
      setAnalytics(existing?.analytics ?? false);
      setMarketing(existing?.marketing ?? false);
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, onOpenPreferences);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, onOpenPreferences);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showDetails) setShowDetails(false);
        else close(false, false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, showDetails, close]);

  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusable[0]?.focus();
  }, [open, showDetails]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:max-w-md z-[9999]
                     rounded-[28px] border border-[rgba(17,17,17,0.08)] bg-[rgba(255,255,255,0.95)] p-5
                     shadow-[0_20px_50px_rgba(17,17,17,0.10)] backdrop-blur-[16px]"
        >
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-brand-navy flex-shrink-0 mt-0.5" aria-hidden />
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-brand-navy">We use cookies</h2>
              <p className="mt-1 text-xs text-[#666666] leading-relaxed">
                We use essential cookies to run this site and optional cookies to understand how you use it.
                See our{" "}
                <Link href="/cookies" className="text-brand-navy underline hover:text-brand-navy/70">
                  cookie policy
                </Link>
                .
              </p>
            </div>
          </div>

          {showDetails && (
            <div className="mt-4 space-y-2 border-t border-[rgba(17,17,17,0.08)] pt-4">
              <div className="flex items-center justify-between p-3 rounded-2xl bg-[#EFEDE8]">
                <div>
                  <p className="text-xs font-medium text-brand-navy">Necessary</p>
                  <p className="text-xs text-[#666666]">Always on</p>
                </div>
                <span className="text-xs font-medium text-brand-navy">Required</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-2xl bg-[#EFEDE8]">
                <div>
                  <p className="text-xs font-medium text-brand-navy">Analytics</p>
                  <p className="text-xs text-[#666666]">Google Analytics, usage insights</p>
                </div>
                <Toggle
                  checked={analytics}
                  onChange={() => setAnalytics((v) => !v)}
                  label="Analytics cookies"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-2xl bg-[#EFEDE8]">
                <div>
                  <p className="text-xs font-medium text-brand-navy">Marketing</p>
                  <p className="text-xs text-[#666666]">Session recording, ads</p>
                </div>
                <Toggle
                  checked={marketing}
                  onChange={() => setMarketing((v) => !v)}
                  label="Marketing cookies"
                />
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => close(true, true)}
              className="px-4 py-2 rounded-full bg-brand-lime text-brand-navy text-xs font-medium min-h-[44px] hover:brightness-95 transition-[filter]"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={() => close(false, false)}
              className="px-4 py-2 rounded-full border border-[rgba(17,17,17,0.15)] text-brand-navy text-xs min-h-[44px] hover:bg-[rgba(17,17,17,0.04)] transition-colors"
            >
              Reject all
            </button>
            {showDetails ? (
              <button
                type="button"
                onClick={() => close(analytics, marketing)}
                className="px-4 py-2 rounded-full text-brand-navy text-xs font-medium min-h-[44px] hover:bg-[rgba(17,17,17,0.04)] transition-colors"
              >
                Save preferences
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 rounded-full text-[#666666] text-xs min-h-[44px] hover:text-brand-navy transition-colors"
              >
                Manage
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
